import { GraphsTabs } from "@focus/focusGrafos";
import { GraphConfigurationProvider } from "@focus/focusGrafos/lib/ConfigurationHandler";
import { GraphDataProvider } from "@focus/focusGrafos/lib/DataHandler";
import {
  FolderType,
  type customTabs,
} from "@focus/focusGrafos/lib/interfaces/config";
import { TimeExtendProvider, useUpdateTime } from "@focus/focusUI";
import { useEffect, useMemo } from "react";
import { useSystemConfig } from "../../hooks/useSystemConfig";
import type { EventEvolutionEvent } from "../events/EventEvolutionTypes";
import { createEvolutionPlotModel } from "./evolutionPlotUtils";
import { LineStyle } from "@focus/focusGrafos/lib/interfaces/element";

interface EvolutionPlotProps {
  criticalRecord?: EventEvolutionEvent;
  id: number;
  records?: EventEvolutionEvent[];
  tca?: string;
}

export const EvolutionPlot = ({
  criticalRecord,
  id,
  records = [],
  tca,
}: EvolutionPlotProps) => {
  const { updateTime } = useUpdateTime();
  const { currentConfig } = useSystemConfig();

  const { plotConfig, plotData } = useMemo(
    () =>
      createEvolutionPlotModel({
        criticalRecord,
        records,
        tca,
        defRiskLevel: currentConfig?.defRiskLevel,
      }),
    [criticalRecord, records, tca, currentConfig?.defRiskLevel],
  );
  const tabs = plotConfig.selectorFolderList[0].selectorList[0]
    .tabs as customTabs[];

  useEffect(() => {
    // Keep the timeline updated with the current time so that the "Now" line is always at the correct position.
    updateTime(Date.now());
  }, [updateTime]);

  return (
    <div
      className="relative h-full w-full overflow-hidden rounded-xl bg-white [&_.focus_grafos_tab_inset]:!left-0 [&_.graph-slider-time-selector]:!px-3 [&_.plotly-plot]:!max-w-none [&_.plotly-plot]:!min-w-0"
      data-evolution-plot-id={id}
    >
      <TimeExtendProvider>
        <GraphConfigurationProvider initialData={plotConfig}>
          <GraphDataProvider initialData={plotData}>
            <GraphsTabs
              data={plotData}
              folderType={FolderType.Defined}
              legendCollapsedOnMount
              tabs={tabs}
              useTimeline
              // Nowline blue and dashed
              timeLineConfig={{color:"#0000ff",lineStyle:LineStyle.dashed}}
            />
          </GraphDataProvider>
        </GraphConfigurationProvider>
      </TimeExtendProvider>
    </div>
  );
};
